import { ChainApprover, ChainLevel } from './types';

/**
 * What a level and its approvers actually mean, derived once.
 *
 * The engine's `status` column is not the whole answer, in two places that both
 * bite on a rejected offer — which is exactly the state somebody opens the chain
 * to read.
 *
 * This module is the single source for that derivation. Three surfaces render
 * the chain — Design Requirements §6 (the offer decision sidebar), §10 (the
 * recruiter read-only view) and the runtime progress panel — and three copies of
 * this function is three chances to disagree about what `skipped` means.
 */

export type LevelState =
  /** Satisfied. */
  | 'approved'
  /** Open and being asked of its approvers right now. */
  | 'active'
  /** An approver refused it. */
  | 'rejected'
  /** Not reached yet — an earlier level in the same rule is still open. */
  | 'notStarted'
  /** Its condition was evaluated and did not match, so nobody was asked. */
  | 'notTriggered'
  /** It applied, and an outcome elsewhere in the request closed it first. */
  | 'cancelled';

/**
 * `skipped` means two unrelated things and the column cannot tell them apart.
 *
 * `Approvals::SubmitRequest` writes `skipped` with `condition_met: false` for a
 * conditional level whose predicate did not match — nobody was ever asked.
 * `Approvals::RecordDecision#close_co_active_steps` *also* writes `skipped`, on
 * every sibling level still open when a rejection short-circuits the request.
 * Those levels did apply; the rejection overtook them.
 *
 * Reading both as "did not apply" tells an approver a level was never relevant
 * when in fact it was cancelled out from under its approvers. `condition_met`
 * is what separates them: only `false` is a genuine non-match.
 */
export const levelState = (
  level: ChainLevel,
  /**
   * Whether the request itself has finished. Required, not optional: a level's
   * own column cannot answer this, and defaulting it to `false` is what produced
   * the two states below.
   *
   * `close_co_active_steps` is scoped to `request.steps.active`, so a rejection
   * closes the levels that were OPEN and leaves later-position ones `pending`.
   * Read alone those render "Not started", whose own copy says "not reached yet,
   * because an earlier level is still open" — nothing is open. And
   * `CancelRequest` touches no steps at all, so an `active` level outlives the
   * request it belongs to.
   */
  requestSettled: boolean,
): LevelState => {
  switch (level.status) {
    case 'approved':
      return 'approved';
    case 'rejected':
      return 'rejected';
    case 'skipped':
      return level.conditionMet === false ? 'notTriggered' : 'cancelled';
    case 'active':
      // An active level on a finished request was never closed by the engine;
      // it simply stopped mattering.
      return requestSettled ? 'cancelled' : 'active';
    default:
      return requestSettled ? 'cancelled' : 'notStarted';
  }
};

/** A level that has closed without this person answering it. */
const isClosedUnanswered = (state: LevelState) =>
  state === 'rejected' || state === 'cancelled';

/**
 * The approvers whose state is worth showing.
 *
 * A backup before escalation is configuration, not a participant —
 * `Approvals::RecordDecision#premature_backup?` refuses their decision — so
 * listing them as awaited overstates who the level is waiting for.
 */
export const visibleApprovers = (level: ChainLevel): ChainApprover[] =>
  level.approvers.filter(approver => !approver.backup || level.escalated);

export type ApproverState = 'approved' | 'rejected' | 'pending' | 'cancelled';

/**
 * How one approver's own state reads inside a level.
 *
 * `pending` depends on whether the level is still running. On a level that has
 * ended, "Pending" claims a decision is still expected when the request is over.
 * Design Requirements §8 calls an approver the outcome overtook "Cancelled", and
 * it is used here for the same reason it is used on the level.
 */
export const approverState = (
  approver: ChainApprover,
  state: LevelState,
): ApproverState => {
  if (approver.status === 'approved') return 'approved';
  if (approver.status === 'rejected') return 'rejected';

  return isClosedUnanswered(state) ? 'cancelled' : 'pending';
};

/**
 * How far along a level that needs several sign-offs is.
 *
 * Mirrors `Approvals::Step#approval_satisfied?`. Under `all` a level needs one
 * sign-off per primary approver. Escalation never lowers that number. It only
 * lets backups give some of those sign-offs. So the total is the primary count
 * before and after escalation. The approvals are the distinct people who
 * approved among `visibleApprovers`, which is the engine's
 * `eligible_approvers`.
 *
 * Only meaningful under `all`: on `any` logic the first approval finishes the
 * level, so a count would describe progress toward a total nobody is waiting for.
 *
 * @return null when a count would mislead
 */
export const levelProgress = (
  level: ChainLevel,
  /**
   * Same argument `levelState` takes, and threaded rather than assumed for the
   * same reason: a cancelled request leaves its open level `active`, so a stubbed
   * `false` here reports live progress on a level whose own pill reads
   * "Cancelled".
   */
  requestSettled: boolean,
): { approved: number; total: number } | null => {
  if (level.approverLogic !== 'all') return null;
  if (levelState(level, requestSettled) !== 'active') return null;

  // One primary is one sign-off, which the first approval supplies.
  const required = level.approvers.filter(approver => !approver.backup).length;
  if (required < 2) return null;

  // People, not assignments, because the engine counts distinct users.
  // `Approvals::ApproverResolver` writes one assignment per person per level,
  // but nothing in the schema enforces it, and a person approving twice is
  // still one sign-off. A row with no user counts for nobody, as there.
  const approvedBy = new Set(
    visibleApprovers(level)
      .filter(
        approver => approver.status === 'approved' && approver.id !== null,
      )
      .map(approver => approver.id),
  );

  return { approved: approvedBy.size, total: required };
};
